/// <amd-module name="Test/TestPlansHubRefresh/TestHub/Component/SuiteTreeConnectedView" />
import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import * as Reselect from "Reselect";
import { ISuitesMenu } from "Test/TestPlansHubRefresh/SuitesMenu/SuitesMenu.Types";
import { TelemetryConstants, TestHubRefreshConstants, SuitesConstants } from "Test/TestPlansHubRefresh/TestHub/Constants";
import { SuitesActions } from "Test/TestPlansHubRefresh/TestHub/Redux/Suites/SuitesActions";
import { ISuitesAwareState, TestSuiteWithCount } from "Test/TestPlansHubRefresh/TestHub/Redux/Suites/SuitesContract";
import { ITestHubRefreshLocalContext } from "Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshContracts";
import * as Resources from "Test/TestPlansHubRefresh/TestHub/Resources";
import * as TestHubResources from "Test/TestPlansHubRefresh/TestHub/Resources";
import { getSelectionSuiteIndexFromSuiteId, getSuiteIdFromSelectedSuiteIndex } from "Test/TestPlansHubRefresh/TestHub/Util";
import { TestHubRefreshLocalContext } from "Test/TestPlansHubRefresh/TestHub/Utils/TestPlansHubRefreshLocalContextProvider";
import { TestSuiteType } from "TestPlanCommon/Contracts/TestPlan";
import { IReadonlyObservableArray, ObservableCollection, ObservableValue } from "VSS/Core/Observable";
import { format, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { getMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssComponentContext, IVssComponentProperties, IVssLayoutManager, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { Header, TitleSize } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Icon } from "VSSUI/Icon";
import { BoltListDragEvent, IListDropData, IListSelection, ListDragDropBehavior, ListDragImage, ListSelection } from "VSSUI/List";
import { ContextualMenu, IMenuItem } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { ConnectedHeaderCommandBar } from "VSSUI/ReduxHeaderCommandBar";
import { ColumnMore } from "VSSUI/Table";
import { TextField } from "VSSUI/TextField";
import { Tooltip } from "VSSUI/TooltipEx";
import { ExpandableTreeCell, ITreeColumn, ITreeRow, Tree } from "VSSUI/TreeEx";
import { KeyCode } from "VSSUI/Util";
import { DragDropEffect } from "VSSUI/Utilities/DragDrop";
import { ITreeItem, ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";
import { TestSuiteTreeItemDataProvider, TestSuiteTreeItemProvider } from "./TestSuiteTreeItemProvider";
import { ISuiteTableItem } from "./TestSuiteTreeItemProvider.Types";
import { TestHubRefreshActions } from "../Redux/TestHubRefresh/TestHubRefreshActions";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { EventDispatch, IEventDispatch } from "VSSUI/Utilities/Dispatch";
import { cellFromEvent } from "VSSUI/List";
import { FilterBar } from "VSSUI/FilterBar";
import { Spinner } from "VSSUI/Spinner";
import { Filter, FILTER_CHANGE_EVENT, FILTER_RESET_EVENT, IFilterState } from "VSSUI/Utilities/Filter";
import { KeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { getId } from "VSS/Core/Util/Object";
import { TelemetryActions } from "Test/TestPlansHubRefresh/TestHub/Redux/TelemetryActions";

interface IFilterSuiteTreeState {
    isSuiteTreeFiltering?: boolean;
}

export interface ISuiteTreeComponentProps extends IVssComponentProperties {
    testPlanName: string;
    testPlanAreaPath: string;
    testPlanIterationPath: string;
    testPlanId: number;
    selectedSuiteId: number;
    testSuites: TestSuiteWithCount[];
    rootSuiteId: number;
    isAdvancedLicenseEnabled: boolean;
    idToTreeItemExpandableStateMap: Map<number, boolean>;
    idToTestSuiteMap: Map<number, TestSuiteWithCount>;
    editableSuiteId: number;
    isShowTestsFromChildSuitesEnabled: boolean;
    selectedBuildDefnId: string;
    // isReload is set when the plan along with suites and cases/points have been reloaded
    isReload: boolean;

    // The react context object. TestPlansHubRefreshConnectedView is the provider of this data.
    testhubRefreshContext?: ITestHubRefreshLocalContext;

    // Required to show unsaved Changes alert to user when in grid view on suite change.
    isInGridView: boolean;
    isGridDirty: boolean;

    // Dispatch Actions
    onSelectedSuite(planId: number, suiteId: number, suiteName: string): void;
    updateTestSuiteName(testSuiteId: number, testSuiteTitle: string, rootSuiteId: number): void;
    changeTreeItemExpandableState(suiteId: number, expandedState: boolean): void;
    saveTestSuiteName(testPlanId: number, testSuiteId: number, oldSuiteTitle: string, newSuiteTitle: string, rootSuiteId: number): void;
    onUpdateReloadState(state: boolean): void;
    updateTestSuiteParent(testPlanId: number, suiteId: number, targetParentSuiteId: number): void;
    reorderTestSuite(testPlanId: number, parentSuiteId: number, draggedSuiteId: number, targetSuiteId: number, dropAtEnd: boolean): void;
    updateParentAndReorderSuite(testPlanId: number, parentSuiteId: number, draggedSuiteId: number, targetSuiteId: number, dropAtEnd: boolean): void;
    showToastNotifications(toastMessage: string, toastDurationMs: number): void;
    // Action to expand or collapse all suites, expandCollapseBool: true->expand all suites, false->collapse all suites
    expandCollapseAllSuites(expandCollapseBool: boolean): void;
    moveTestCaseToSuite(
        testPlanId: number,
        sourceSuiteId: number,
        sourceSuiteRevision: number,
        destinationSuiteId: number,
        destinationSuiteRevision: number,
        testCaseIds: number[]
    ): void;
    showTestsFromChildSuitesClick(planId: number, selectedSuiteId: number, isShowTestsFromChildSuitesEnabled: boolean): void;
    fetchTestCasesCount(planId: number): void;
    closeGrid(planId: number, selectedSuiteId: number): void;
    filterTestSuites(keyword: string, callback: any): void;
    // Publishes telemetry.
    publishTelemetry(action: string, payload: { [key: string]: any }): void;
}

export class SuiteTreeView extends VssComponent<ISuiteTreeComponentProps, IFilterSuiteTreeState> {
    private selection: IListSelection;
    private contextMenuSuiteId: number;
    private _textField = React.createRef<TextField>();
    private _suiteNameObservable = new ObservableValue<string>("");
    private _dragDropBehavior: ListDragDropBehavior<ITreeItemEx<ISuiteTableItem>>;
    private _showPanel: ObservableValue<boolean>;
    private _eventDispatch: IEventDispatch;
    // dragDropTimerId is used for setting and removing timeouts for drag drop suites scenarios
    private _isSuitesSortEnabled: boolean;
    private _context: IVssPageContext;

    private _dragDropTimerId: number;
    private _filter: Filter;
    private _filterKey = "suiteFilter-" + getId();
    private _filterBarRef = React.createRef<FilterBar>();
    constructor(props: ISuiteTreeComponentProps, context: IVssComponentContext) {
        super(props, context);
        this.selection = new ListSelection();
        this._eventDispatch = new EventDispatch();
        this._registerKeyboardShortcuts(this.props);
        this._setInitialSelection();
        this._context = context.pageContext;
        this._dragDropBehavior = new ListDragDropBehavior<ITreeItemEx<ISuiteTableItem>>({
            allowedTypes: [SuitesConstants.TestSuiteDragItemType, SuitesConstants.TestCaseDragItemType],
            id: "test-suite-tree",
            onDragStart: this._onDragStart,
            onDrop: this._onDrop,
            onDragOver: this._onDragOver,
            renderDragImage: (event) => {
                const treeItem = event.detail.dataTransfer.data.underlyingItem.data as ISuiteTableItem;
                return <ListDragImage text={treeItem.title} />;
            },
            type: SuitesConstants.TestSuiteDragItemType
        });
        this._showPanel = new ObservableValue<boolean>(false);
        this._onRightClick = this._onRightClick.bind(this);
        this._eventDispatch.addEventListener("contextmenu", this._onRightClick);
        this.state = {
            isSuiteTreeFiltering: false
        };
        this._isSuitesSortEnabled = isFeatureFlagEnabled(
            this._context,
            TestHubRefreshConstants.SortPlansAndSuitesAlphabeticallyFF,
            false
        );

        this._filter = new Filter();
    }

    private _onTreeFilterReset = (): void => {
        this.setState({
            isSuiteTreeFiltering: true
        })
        this.props.filterTestSuites('', () => {
            this.setState({ isSuiteTreeFiltering: false })
        });
    }

    private _onTreeFilterChanged = (changedFilter: IFilterState): void => {
        // calculate filtertext here
        if (changedFilter.hasOwnProperty(this._filterKey)) {
            const filterState = changedFilter[this._filterKey];
            const filterText: string = filterState ? filterState.value : "";

            this.setState({
                isSuiteTreeFiltering: true
            })
            this.props.filterTestSuites(filterText, () => {
                this.setState({
                    isSuiteTreeFiltering: false
                })
            });

            if (filterText.length === 1) {
                const telemetryPayload = {
                    testPlanId: this.props.testPlanId
                };
                this.props.publishTelemetry(TelemetryConstants.FILTER_TESTSUITE_TREE, telemetryPayload);
            }
        }
    };

    public componentDidMount() {
        this._filter.subscribe(this._onTreeFilterChanged, FILTER_CHANGE_EVENT);
        this._filter.subscribe(this._onTreeFilterReset, FILTER_RESET_EVENT);
        if (this._filterBarRef.current) {
            this._filterBarRef.current.focus();
        }
    }

    public shouldComponentUpdate(nextProps: ISuiteTreeComponentProps) {
        if (this.props.selectedSuiteId !== nextProps.selectedSuiteId) {
            this._registerKeyboardShortcuts(nextProps);
        }
        // If isReload is true, then we need to reselect the suite for the new set.It also needs to dispatch an action to reset the isReload state.
        if (nextProps.isReload) {
            // test suite selection in case of test plan is changed from test plan segment OR test plan is cloned.
            if (this.props.testSuites && this.props.testSuites.length > 0) {
                if (this.props.testSuites[0].children && this.props.testSuites[0].children.length > 0) {
                    this.selection.select(1);
                } else {
                    this.selection.select(0);
                }
            }
            this.props.onUpdateReloadState(false);
        }
        return true;
    }

    public componentWillUnmount(): void {
        const shortcutProviderService = this.context.pageContext.getService("SuiteTreeKeyboardShortcutProvider") as any;
        shortcutProviderService.unregisterKeyboardShortcuts();
        this._filter.unsubscribe(this._onTreeFilterChanged, FILTER_CHANGE_EVENT);
        this._filter.unsubscribe(this._onTreeFilterReset, FILTER_RESET_EVENT);
    }

    public componentDidUpdate(): void {
        this._selectTextField();
    }

    public render(): JSX.Element {
        let suites: TestSuiteWithCount[] = [];
        let planName = "";
        let rootSuiteId = -1;
        let editableSuiteId = -1;
        let expandableStateMap: Map<number, boolean> = new Map();

        if (this.props.testSuites) {
            planName = this.props.testPlanName;
            rootSuiteId = this.props.rootSuiteId;
            suites = this.props.testSuites;
            expandableStateMap = this.props.idToTreeItemExpandableStateMap;
            editableSuiteId = this.props.editableSuiteId;
        }

        let elm: JSX.Element = {} as JSX.Element;

        if (suites) {
            const testSuiteNewTreeItemDataProvider = new TestSuiteTreeItemDataProvider();
            const items: ITreeItem<ISuiteTableItem>[] = testSuiteNewTreeItemDataProvider.populateItems(
                suites,
                rootSuiteId,
                expandableStateMap,
                editableSuiteId,
                planName,
                this._isSuitesSortEnabled
            );
            //adding this condition to make the appropriate selection on suite rename and Suite id change in URL
            //also the new logic behind feature flag will sort the suite every time when is a rename/ creation of suite.
            if (this._isSuitesSortEnabled) {
                const selectedSuiteIndex = getSelectionSuiteIndexFromSuiteId(
                    this.props.selectedSuiteId,
                    this.props.testSuites,
                    -1,
                    this.props.idToTreeItemExpandableStateMap
                );
                if (selectedSuiteIndex && selectedSuiteIndex.found && !this.selection.selected(selectedSuiteIndex.index)) {
                    this.selection.select(selectedSuiteIndex.index);
                }
            }
            const provider = new TestSuiteTreeItemProvider(items);

            elm = (
                <div className="suite-tree-segment">
                    <div className="suite-tree-parent-container flex-grow flex-column left-tabs">
                        <div className="testhub-refresh-tree-container">
                            <Header
                                className="testplans-hub-refresh-near-content-header title-s"
                                title={TestHubResources.Suites}
                                titleSize={TitleSize.Medium}
                                titleAriaLevel={2}
                            >
                                <div className="suite-tree-header-command-bar">
                                    <ConnectedHeaderCommandBar
                                        showFilter={false}
                                        commandsSelector={CommandSelector}
                                        viewActionsSelector={this._viewActions}
                                    />
                                </div>
                            </Header>

                            <div style={{ paddingLeft: '7px', paddingRight: '5px' }}>
                                <FilterBar filter={this._filter} className={"flex-center"} ref={this._filterBarRef} role={Resources.SearchAriaLabel}  >
                                    <KeywordFilterBarItem placeholder={Resources.FilterSuitesByName} filterItemKey={this._filterKey} />
                                </FilterBar>
                            </div>

                            {this.state.isSuiteTreeFiltering ? <Spinner /> : (suites.length > 0 ?
                                <Tree
                                    eventDispatch={this._eventDispatch}
                                    containerClassName="testhub-refresh-tree custom-scrollbar"
                                    className="testhub-refresh-tree"
                                    showHeader={false}
                                    columns={this._getColumns()}
                                    itemProvider={provider}
                                    showLines={false}
                                    singleClickActivation={false}
                                    onToggle={(event: any, treeItem: ITreeItemEx<ISuiteTableItem>) => {
                                        this._onToggleTreeItem(provider, treeItem);
                                    }}
                                    onSelect={(event: any, treeRow: ITreeRow<ISuiteTableItem>) => {
                                        this._onSelectedItemsChanged(treeRow.data.underlyingItem.data.id, treeRow.data.underlyingItem.data.title);
                                    }}
                                    selection={this.selection}
                                    scrollable={true}
                                    behaviors={[this._dragDropBehavior]}
                                />
                                :
                                <div className="no-results-msg">
                                    {Resources.SuitesNotFoundMessage}
                                </div>
                            )}
                        </div>
                    </div>
                    <Observer showPanel={this._showPanel}>
                        {(props: { showPanel: boolean }) => {
                            return (
                                props.showPanel && (
                                    <WrappedComponent
                                        dependencies={["ms.vss-test-web.testsuite-import-panel"]}
                                        wrappedType="import-test-suite-panel"
                                        wrappedProps={{
                                            showPanel: this._showPanel,
                                            destinationSuiteId: this.contextMenuSuiteId,
                                            destinationPlanId: this.props.testPlanId,
                                            destinationAreaPath: this.props.testPlanAreaPath,
                                            destinationIterationPath: this.props.testPlanIterationPath
                                        }}
                                    />
                                )
                            );
                        }}
                    </Observer>
                </div>
            );
        }
        return elm;
    }

    // Invoked on selecting a suite tree item
    public _onSelectedItemsChanged = (selectedSuiteId: number, selectedSuiteName: string) => {
        let gridChangesSaved: boolean = true;
        if (this.props.isInGridView && this.props.isGridDirty) {
            gridChangesSaved = confirm(Resources.UnsavedChangesMessage);
        }
        if (!this.props.isInGridView || !this.props.isGridDirty || gridChangesSaved) {
            // start TTI telemetry scenario for non-page load case
            if (this.props.testhubRefreshContext) {
                const currentPivot: string | undefined = this.props.testhubRefreshContext.getCurrentPivot();

                if (currentPivot) {
                    let scenarioName: string | undefined = undefined;

                    if (localeIgnoreCaseComparer(currentPivot, TestHubRefreshConstants.DEFINE_TAB_ID) === 0) {
                        scenarioName = TelemetryConstants.AUTHOR_PIVOT_PAGE_LOAD_TTI_SCENARIO;
                    } else if (localeIgnoreCaseComparer(currentPivot, TestHubRefreshConstants.EXECUTE_TAB_ID) === 0) {
                        scenarioName = TelemetryConstants.EXECUTE_PIVOT_PAGE_LOAD_TTI_SCENARIO;
                    }

                    if (scenarioName) {
                        const properties = {
                            ttiScenario: "SuiteTreeSelectionChanged"
                        };
                        const performanceSerivce: IVssPerformanceService = this.context.pageContext.getService<IVssPerformanceService>(
                            "IVssPerformanceService"
                        );
                        performanceSerivce.startScenario(scenarioName, true, undefined, properties);
                    }
                }
            }

            this.props.onSelectedSuite(this.props.testPlanId, selectedSuiteId, selectedSuiteName);
        } else {
            // update the selection on suite tree
            const indexInfo = getSelectionSuiteIndexFromSuiteId(
                this.props.selectedSuiteId,
                this.props.testSuites,
                -1,
                this.props.idToTreeItemExpandableStateMap
            );
            this.selection.select(indexInfo.index);
        }

        if (this.props.isInGridView && gridChangesSaved) {
            this.props.closeGrid(this.props.testPlanId, this.props.selectedSuiteId);
        }
    };

    // Render the tree title column in the tree
    public _renderCell = (
        rowIndex: number,
        columnIndex: number,
        treeColumn: ITreeColumn<ISuiteTableItem>,
        treeItem: ITreeItemEx<ISuiteTableItem>
    ): JSX.Element => {
        const { underlyingItem } = treeItem;
        const title = underlyingItem.data.title;
        let icon: JSX.Element | undefined = undefined;
        let iconName = "";
        let text: JSX.Element | string = title;
        const currentPivot: string | undefined = this.props.testhubRefreshContext ? this.props.testhubRefreshContext.getCurrentPivot() : undefined;
        let count = 0;
        let totalCount = 0;
        let filterSet: boolean | undefined = false;
        if (currentPivot === TestHubRefreshConstants.DEFINE_TAB_ID) {
            count = underlyingItem.data.testCaseCount ? underlyingItem.data.testCaseCount : 0;
            totalCount = underlyingItem.data.totalTestCaseCount ? underlyingItem.data.totalTestCaseCount : 0;
            filterSet = this.props.testhubRefreshContext && this.props.testhubRefreshContext.isAuthorFilterActiveOrSet();
        } else if (currentPivot === TestHubRefreshConstants.EXECUTE_TAB_ID) {
            count = underlyingItem.data.pointCount ? underlyingItem.data.pointCount : 0;
            totalCount = underlyingItem.data.totalPointCount ? underlyingItem.data.totalPointCount : 0;
            filterSet = this.props.testhubRefreshContext && this.props.testhubRefreshContext.isExecuteFilterActiveOrSet();
        }
        let countString = "";
        if (totalCount && filterSet) {
            countString = format(TestHubResources.SuiteEntityCountWithFilter, count, totalCount);
        } else if (totalCount && !filterSet) {
            countString = format(TestHubResources.SuiteEntityCountNoFilter, totalCount);
        }

        const suiteToolTip = countString ? title + " " + countString : title;
        if (underlyingItem.data.isInEditMode) {
            // assign text value to suite title in case of only in case of new suite creation/rename
            if (this._suiteNameObservable.value.trim() === "") {
                this._suiteNameObservable.value = title;
            }
            text = (
                <TextField
                    value={this._suiteNameObservable}
                    ref={this._textField}
                    onChange={(e, newValue) => (this._suiteNameObservable.value = newValue)}
                    autoFocus={true}
                    onKeyDown={(event) => event.stopPropagation()} // To avoid tree component to consume event when suite is in editable mode
                    onKeyPress={(event: React.KeyboardEvent<HTMLInputElement>) => {
                        // saving the suite in case of enter press
                        this._onKeyPress(event, title, underlyingItem.data.id);
                    }}
                    onBlur={() => {
                        // when the focus is removed from the text field, it should save the test suite
                        if (this._suiteNameObservable.value.trim() === "") {
                            this._suiteNameObservable.value = title;
                        }
                        this.props.saveTestSuiteName(
                            this.props.testPlanId,
                            underlyingItem.data.id,
                            title,
                            this._suiteNameObservable.value,
                            this.props.rootSuiteId
                        );
                        const indexInfo = getSelectionSuiteIndexFromSuiteId(
                            underlyingItem.data.id,
                            this.props.testSuites,
                            -1,
                            this.props.idToTreeItemExpandableStateMap
                        );

                        // Change the selected suite to the suited being renamed if not currently selected
                        const shouldChangeSuite = underlyingItem.data.id !== this.props.selectedSuiteId;
                        if (shouldChangeSuite) {
                            this.selection.select(indexInfo.index);
                            this.props.onSelectedSuite(this.props.testPlanId, underlyingItem.data.id, this._suiteNameObservable.value);
                        }
                        this._suiteNameObservable.value = "";
                    }}
                />
            );
        }
        if (underlyingItem.data.hasParent) {
            if (underlyingItem.data.type === TestSuiteType.DynamicTestSuite) {
                iconName = "FolderQuery";
            } else if (underlyingItem.data.type === TestSuiteType.StaticTestSuite) {
                iconName = "FolderHorizontal";
            } else {
                iconName = "FolderList";
            }

            icon = <Icon iconName={iconName} className="test-suite-folder-icon" />;
        }
        const content: JSX.Element = (
            <span className="test-suite-content">
                {icon}
                <Tooltip overflowOnly={true} text={suiteToolTip}>
                    <div className="text-ellipsis">
                        <span>{text}</span>
                    </div>
                </Tooltip>
                <Tooltip overflowOnly={true} text={suiteToolTip}>
                    <div className="text-ellipsis">
                        &nbsp;
                        {countString && !underlyingItem.data.isInEditMode && <span className="suite-entity-count">{countString}</span>}
                    </div>
                </Tooltip>
            </span>
        );

        return ExpandableTreeCell({
            children: content,
            columnIndex,
            contentClassName: undefined,
            treeColumn,
            treeItem
        });
    };

    // Get columns for the suites tree.
    public _getColumns() {
        const columns = [];
        columns.push({
            id: "title",
            renderCell: this._renderCell,
            width: -150
        });

        if (this.props.editableSuiteId === -1 && this.props.isAdvancedLicenseEnabled) {
            const menuItemColumn = new ColumnMore((treeItem: ITreeItemEx<ISuiteTableItem>) => {
                return {
                    id: "list-context-menu",
                    items: this._getContextualSuitesItems(treeItem.underlyingItem.data)
                };
            });
            columns.push(menuItemColumn);
        }
        return columns;
    }

    // callback for any key press when the tree is in editable state
    public _onKeyPress = (event: React.KeyboardEvent<HTMLInputElement>, oldSuiteTitle: string, suiteId: number) => {
        if (event.which === KeyCode.enter) {
            {
                if (this._suiteNameObservable.value.trim() === "") {
                    this._suiteNameObservable.value = oldSuiteTitle;
                }
                this.props.saveTestSuiteName(this.props.testPlanId, suiteId, oldSuiteTitle, this._suiteNameObservable.value, this.props.rootSuiteId);
                // select the suite which is being renamed if the same is not currently selected
                const indexInfo = getSelectionSuiteIndexFromSuiteId(suiteId, this.props.testSuites, -1, this.props.idToTreeItemExpandableStateMap);
                const shouldChangeSuite = suiteId !== this.props.selectedSuiteId;
                if (indexInfo.found && shouldChangeSuite) {
                    this.selection.select(indexInfo.index);
                    this.props.onSelectedSuite(this.props.testPlanId, suiteId, this._suiteNameObservable.value);
                }
                this._suiteNameObservable.value = "";
            }
        }
    };

    public _onToggleTreeItem(provider: TestSuiteTreeItemProvider, treeItem: ITreeItemEx<ISuiteTableItem>) {
        provider.toggle(treeItem.underlyingItem);
        const suiteId = treeItem.underlyingItem.data.id;
        const expandedState = treeItem.underlyingItem.expanded as boolean;
        this.props.changeTreeItemExpandableState(suiteId, expandedState);
    }

    // Get Contextual suites items for the entries in the suite tree
    public _getContextualSuitesItems = (treeItem: ISuiteTableItem): IReadonlyObservableArray<IMenuItem> => {
        const menuItems: ObservableCollection<IMenuItem> = new ObservableCollection();
        const props: ISuitesMenu = {
            planId: this.props.testPlanId,
            rootSuiteId: this.props.rootSuiteId,
            suite: treeItem,
            dispatch: this.props.dispatch,
            planAreaPath: this.props.testPlanAreaPath,
            selection: this.selection,
            testSuiteImportPanelOpen: this._testSuiteImportPanelOpen
        };

        this.contextMenuSuiteId = treeItem.id;

        if (this.props.isAdvancedLicenseEnabled) {
            if (props.suite.hasParent === true) {
                menuItems.push(getMenuItems(this.context.pageContext, "ms.vss-test-web.suite-list-menu", props));
            } else {
                menuItems.push(getMenuItems(this.context.pageContext, "ms.vss-test-web.root-suite-list-menu", props));
            }

            if (isFeatureFlagEnabled(this.context.pageContext, TestHubRefreshConstants.EnableThirdPartyMenuContributionsFF, false)) {
                const selectedSuite = this.props.idToTestSuiteMap.get(treeItem.id) as TestSuiteWithCount;
                if (selectedSuite) {
                    // contextual data required by menu extensions
                    const extensionContext = {
                        plan: {
                            id: this.props.testPlanId,
                            name: this.props.testPlanName,
                            area: { name: this.props.testPlanAreaPath },
                            iteration: this.props.testPlanIterationPath,
                            owner: null,
                            revision: 0,
                            rootSuite: { id: this.props.rootSuiteId },
                            areaPath: this.props.testPlanAreaPath,
                            text: format("{0} ({1}: {2})", this.props.testPlanName, TestHubResources.ID, this.props.testPlanId),
                            rootSuiteId: this.props.rootSuiteId,
                            buildUri: "",
                            buildDefinitionId: this.props.selectedBuildDefnId,
                            releaseEnvironmentDefinition: null
                        },
                        suite: {
                            id: selectedSuite.id,
                            revision: selectedSuite.revision,
                            name: selectedSuite.name,
                            pointCount: selectedSuite.pointCount,
                            suiteType: selectedSuite.suiteType,
                            requirementId: selectedSuite.requirementId,
                            parentSuiteId: selectedSuite.parentSuite ? selectedSuite.parentSuite.id : 0,
                            queryText: selectedSuite.queryString,
                            configurations: selectedSuite.defaultConfigurations,
                            children: selectedSuite.children,
                            totalPointCount: selectedSuite.totalPointCount,
                            type: selectedSuite.suiteType,
                            title: selectedSuite.name
                        }
                    };
                    //adding 3rd party menu which are targetting id - test-plans-suites-context
                    menuItems.push(getMenuItems(this.context.pageContext, "ms.vss-test-web.test-plans-suites-context", extensionContext));
                }
            }
        }
        return menuItems;
    };

    private _viewActions = (): IHeaderCommandBarItem[] => {
        const commands: IHeaderCommandBarItem[] = [];
        if (this.props.testhubRefreshContext) {
            const currentPivot: string | undefined = this.props.testhubRefreshContext.getCurrentPivot();
            if (currentPivot === TestHubRefreshConstants.EXECUTE_TAB_ID) {
                commands.push({
                    ariaLabel: Resources.ShowTestsFromChildSuites,
                    id: "show-test-child-suites",
                    iconProps: {
                        iconName: "RowsChild"
                    },
                    subtle: !this.props.isShowTestsFromChildSuitesEnabled,
                    onActivate: () => {
                        this.props.showTestsFromChildSuitesClick(
                            this.props.testPlanId,
                            this.props.selectedSuiteId,
                            !this.props.isShowTestsFromChildSuitesEnabled
                        );
                    },
                    className: this.props.isShowTestsFromChildSuitesEnabled ? "show-test-child-suites" : "",
                    tooltipProps: {
                        text: Resources.ShowTestsFromChildSuites
                    }
                });
            }
        }
        commands.push(
            {
                ariaLabel: Resources.ExpandAll,
                id: "expand-all",
                iconProps: {
                    iconName: "ExploreContent"
                },
                subtle: true,
                onActivate: () => {
                    this.props.expandCollapseAllSuites(true);
                },
                className: "",
                tooltipProps: {
                    text: Resources.ExpandAll
                }
            },
            {
                ariaLabel: Resources.CollapseAll,
                id: "collapse-all",
                iconProps: {
                    iconName: "SemanticZoom"
                },
                subtle: true,
                onActivate: () => {
                    this.props.expandCollapseAllSuites(false);
                },
                className: "",
                tooltipProps: {
                    text: Resources.CollapseAll
                }
            }
        );
        return commands;
    };

    private _registerKeyboardShortcuts(props: ISuiteTreeComponentProps): void {
        const contributionService: IVssContributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        contributionService.getContributionAsync("ms.vss-test-web.keyboard-shortcuts-module").then(() => {
            const shortcutProviderService = this.context.pageContext.getService("SuiteTreeKeyboardShortcutProvider") as any;
            const curTestSuiteId = props.selectedSuiteId;
            const testSuite = props.idToTestSuiteMap.get(curTestSuiteId) as TestSuiteWithCount;
            if (testSuite) {
                const hasParent = testSuite.parentSuite ? true : false;
                const curTestSuite: ISuiteTableItem = {
                    title: testSuite.name,
                    type: testSuite.suiteType,
                    id: testSuite.id,
                    hasParent: hasParent,
                    isInEditMode: true
                };
                const params: ISuitesMenu = {
                    planId: this.props.testPlanId,
                    rootSuiteId: this.props.rootSuiteId,
                    suite: curTestSuite,
                    dispatch: this.props.dispatch,
                    planAreaPath: this.props.testPlanAreaPath,
                    selection: this.selection
                };
                shortcutProviderService.registerKeyboardShortcuts(params);
            }
        });
    }

    public _testSuiteImportPanelOpen = () => {
        this._showPanel.value = true;
    };

    private _onDragStart = (event: BoltListDragEvent<HTMLElement, ITreeItemEx<ISuiteTableItem>>) => {
        if (this.props.isShowTestsFromChildSuitesEnabled && this.props.testhubRefreshContext) {
            const currentPivot: string | undefined = this.props.testhubRefreshContext.getCurrentPivot();
            if (currentPivot) {
                if (localeIgnoreCaseComparer(currentPivot, TestHubRefreshConstants.EXECUTE_TAB_ID) === 0) {
                    event.detail.dataTransfer.effectAllowed = DragDropEffect.none;
                    this.props.showToastNotifications(
                        TestHubResources.DisableShowTestsFromChildSuites,
                        TestHubRefreshConstants.TOAST_MESSAGE_DURATION
                    );
                }
            }
        } else {
            const dragItem = event.detail.dataTransfer.data;
            const suiteId = dragItem.underlyingItem.data.id;
            // Root suite cannot be dragged
            if (suiteId === this.props.rootSuiteId) {
                event.detail.dataTransfer.effectAllowed = DragDropEffect.none;
            }
            // Selection is changed to the dragged suite tree item
            if (event.detail.dataTransfer.secondaryData && event.detail.dataTransfer.secondaryData.index !== 0) {
                const dragSourceIndex = event.detail.dataTransfer.secondaryData.index;
                this.selection.select(dragSourceIndex);
            }
        }
    };

    private _onDrop = (event: BoltListDragEvent<HTMLElement, ITreeItemEx<ISuiteTableItem>>, dropData: IListDropData) => {
        const dragItem = event.detail.dataTransfer.data as any;
        const dropTargetIndex = dropData.index;
        let dropAtEnd = false;
        // Finding the test suite where the dragged test suite has been dropped
        let targetSuiteInfo = getSuiteIdFromSelectedSuiteIndex(dropTargetIndex, this.props.testSuites, this.props.idToTreeItemExpandableStateMap);

        if (event.detail.dataTransfer.type === SuitesConstants.TestCaseDragItemType) {
            if (!this.selection.selectedCount) {
                event.detail.dataTransfer.effectAllowed = DragDropEffect.none;
                //Show toast notification if user tries to drop the test cases before the target test suite is highlighted
                this.props.showToastNotifications(TestHubResources.HoldCursor, TestHubRefreshConstants.TOAST_MESSAGE_DURATION);
            } else {
                const sourceTestSuite = this.props.idToTestSuiteMap.get(this.props.selectedSuiteId) as TestSuiteWithCount;
                const destinationTestSuite = this.props.idToTestSuiteMap.get(targetSuiteInfo.suiteId) as TestSuiteWithCount;
                if (
                    (sourceTestSuite && sourceTestSuite.suiteType === TestSuiteType.DynamicTestSuite) ||
                    (destinationTestSuite && destinationTestSuite.suiteType === TestSuiteType.DynamicTestSuite)
                ) {
                    event.detail.dataTransfer.effectAllowed = DragDropEffect.none;
                    //Drag drop test case not allowed on QBS
                    this.props.showToastNotifications(TestHubResources.CannotDragDropOnQBS, TestHubRefreshConstants.TOAST_MESSAGE_DURATION);
                } else {
                    let selectedtestCaseIds: number[] = [];
                    // fetching the selected test case ids in test case table which are being dropped on a test suite
                    if (this.props.testhubRefreshContext) {
                        selectedtestCaseIds = this.props.testhubRefreshContext.getSelectedTestCaseIds();
                    }
                    this.props.moveTestCaseToSuite(
                        this.props.testPlanId,
                        this.props.selectedSuiteId,
                        sourceTestSuite.revision,
                        targetSuiteInfo.suiteId,
                        destinationTestSuite.revision,
                        selectedtestCaseIds
                    );
                    const selectedSuiteIndex = getSelectionSuiteIndexFromSuiteId(
                        this.props.selectedSuiteId,
                        this.props.testSuites,
                        -1,
                        this.props.idToTreeItemExpandableStateMap
                    );
                    if (selectedSuiteIndex.found) {
                        this.selection.select(selectedSuiteIndex.index);
                    }
                }
            }
        } else {
            const draggedSuiteId = dragItem.underlyingItem.data.id;
            // suiteId = 0 indicates suite is dropped at the end of the suite tree
            if (!targetSuiteInfo.suiteId) {
                targetSuiteInfo = getSuiteIdFromSelectedSuiteIndex(
                    dropTargetIndex - 1,
                    this.props.testSuites,
                    this.props.idToTreeItemExpandableStateMap
                );
                dropAtEnd = true;
            }
            /**
             * targetTestSuite is parent suite if the suite is dropped inside
             * targetTestSuite is the suite just below the position where dragged test suite is dropped
             */
            const targetTestSuite = this.props.idToTestSuiteMap.get(targetSuiteInfo.suiteId) as TestSuiteWithCount;
            // If suite is not selected, it means dropped suite should be moved to the drop indicator position
            if (!this.selection.selectedCount) {
                const draggedTestSuite = this.props.idToTestSuiteMap.get(draggedSuiteId) as TestSuiteWithCount;
                /**
                 * If test suite is dropped in the same tree level, will just reorder the test suite, otherwise firstly parent test suite
                 * is updated then reordering of test suite is done
                 */
                if (targetTestSuite.parentSuite.id === draggedTestSuite.parentSuite.id) {
                    this.props.reorderTestSuite(this.props.testPlanId, targetTestSuite.parentSuite.id, draggedSuiteId, targetTestSuite.id, dropAtEnd);
                } else {
                    /**
                     * Target suite is the one below the reordering line in the suite tree. Reordering at the end in the same suite level is not
                     * handled with that implementation (Feedback ticket - 1786759). Considering above test suite as well for this case.
                     */
                    let aboveTargetSuiteInfo = getSuiteIdFromSelectedSuiteIndex(
                        dropTargetIndex - 1,
                        this.props.testSuites,
                        this.props.idToTreeItemExpandableStateMap
                    );
                    const aboveTargetTestSuite = this.props.idToTestSuiteMap.get(aboveTargetSuiteInfo.suiteId) as TestSuiteWithCount;
                    if (aboveTargetTestSuite?.parentSuite?.id === draggedTestSuite.parentSuite.id && targetTestSuite.parentSuite.id !== aboveTargetTestSuite?.id) {
                        this.props.reorderTestSuite(this.props.testPlanId, aboveTargetTestSuite.parentSuite.id, draggedSuiteId, aboveTargetTestSuite.id, true);
                    } else {
                        this.props.updateParentAndReorderSuite(
                            this.props.testPlanId,
                            targetTestSuite.parentSuite.id,
                            draggedSuiteId,
                            targetTestSuite.id,
                            dropAtEnd
                        );
                    }
                }
            }
            // If the test suite on which the dragged test suite is being dropped is selected, then it indicates dragged suite should be dropped inside the selected suite
            else {
                if (targetSuiteInfo.suiteId) {
                    // Cannot drop the test suite when parent suite is RBS or QBS
                    if (targetTestSuite && targetTestSuite.suiteType === TestSuiteType.StaticTestSuite) {
                        this.selection.select(dropData.index);
                        this.props.updateTestSuiteParent(this.props.testPlanId, draggedSuiteId, targetSuiteInfo.suiteId);
                    } else {
                        this.props.showToastNotifications(TestHubResources.CanDropOnStaticSuite, TestHubRefreshConstants.TOAST_MESSAGE_DURATION);
                    }
                }
            }
        }
    };

    /**
     * To differentiate whether the suite needs to be placed adjacent to the hovered suite Or should be placed inside the suite, _onDragOver is
     * implemented. If dragged suite is hovered on a particular suite for 1 sec, suite is highlighted and the drop would put the suite inside
     * the highlighted suite
     */
    private _onDragOver = (event: BoltListDragEvent<HTMLElement, ITreeItemEx<ISuiteTableItem>>, dropData: IListDropData) => {
        // If suite is hovered for 700ms then suite will get selected
        const timeoutInterval = 700;
        clearTimeout(this._dragDropTimerId);
        this.selection.clear();
        const suiteSelection = this.selection;
        this._dragDropTimerId = setTimeout(function () {
            selectHoveredSuite(dropData.index, suiteSelection);
        }, timeoutInterval);
    };

    // select all the text when suite name is in editable mode
    private _selectTextField = () => {
        if (this._textField.current) {
            this._textField.current.select();
        }
    };

    private _setInitialSelection() {
        const indexInfo = getSelectionSuiteIndexFromSuiteId(
            this.props.selectedSuiteId,
            this.props.testSuites,
            -1,
            this.props.idToTreeItemExpandableStateMap
        );

        // In case of reload, setting the suite tree item selection to the suiteId mentioned in the url
        this.selection.select(indexInfo.index);
        const testSuite = this.props.idToTestSuiteMap.get(this.props.selectedSuiteId) as TestSuiteWithCount;
        if (testSuite) {
            const testSuiteTitle = testSuite.name;
            this.props.updateTestSuiteName(this.props.selectedSuiteId, testSuiteTitle, this.props.rootSuiteId);
        }
    }

    private _onRightClick(event: React.MouseEvent<HTMLElement>) {
        if (event.button === TestHubRefreshConstants.RIGHT_MOUSE_BUTTON) {
            event.preventDefault();

            const { cellElement, rowIndex } = cellFromEvent(event);

            if (rowIndex >= 0) {
                const testSuiteNewTreeItemDataProvider = new TestSuiteTreeItemDataProvider();
                const items: ITreeItem<ISuiteTableItem>[] = testSuiteNewTreeItemDataProvider.populateItems(
                    this.props.testSuites,
                    this.props.rootSuiteId,
                    this.props.idToTreeItemExpandableStateMap,
                    this.props.editableSuiteId,
                    this.props.testPlanName,
                    this._isSuitesSortEnabled
                );
                //adding this condition to make the appropriate selection on suite rename and Suite id change in URL
                //also the new logic behind feature flag will sort the suite every time when is a rename/ creation of suite.
                if (this._isSuitesSortEnabled) {
                    const selectedSuiteIndex = getSelectionSuiteIndexFromSuiteId(
                        this.props.selectedSuiteId,
                        this.props.testSuites,
                        -1,
                        this.props.idToTreeItemExpandableStateMap
                    );
                    if (selectedSuiteIndex && selectedSuiteIndex.found && !this.selection.selected(selectedSuiteIndex.index)) {
                        this.selection.select(selectedSuiteIndex.index);
                    }
                }
                const provider = new TestSuiteTreeItemProvider(items);

                const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                layoutManager.renderCallout((onDismiss) => {
                    return React.createElement(
                        ContextualMenu,
                        {
                            menuProps: {
                                id: "test-suite-context-menu-column",
                                items: this._getContextualSuitesItems(provider.value[rowIndex].underlyingItem.data)
                            },
                            anchorElement: event.currentTarget as HTMLElement,
                            anchorPoint: { x: event.pageX, y: event.pageY },
                            onDismiss: onDismiss
                        },
                        null
                    );
                });
            }
        }
    }
}

function mapStateToProps(state: ISuitesAwareState): Partial<ISuiteTreeComponentProps> {
    return {
        testPlanName: state.testHubRefreshState.testPlanName,
        testPlanId: state.testHubRefreshState.testPlanId,
        selectedSuiteId: state.testHubRefreshState.selectedSuiteId,
        testSuites: state.suiteState.testSuites,
        rootSuiteId: state.testHubRefreshState.rootSuiteId,
        isAdvancedLicenseEnabled: state.testHubRefreshState.isAdvancedExtensionEnabled,
        testPlanAreaPath: state.testHubRefreshState.testPlanAreaPath,
        testPlanIterationPath: state.testHubRefreshState.testPlanIteration,
        idToTreeItemExpandableStateMap: state.suiteState.idToTreeItemExpandableStateMap,
        idToTestSuiteMap: state.suiteState.idToTestSuiteMap,
        editableSuiteId: state.suiteState.editableSuiteId,
        isReload: state.testHubRefreshState.isReload,
        selectedBuildDefnId: state.testHubRefreshState.selectedBuildDefnId,
        isShowTestsFromChildSuitesEnabled: state.testHubRefreshState.isShowTestsFromChildSuitesEnabled,
        isInGridView: state.testHubRefreshState.isInGridView,
        isGridDirty: state.testHubRefreshState.isGridDirty
    };
}

export const CommandSelector = Reselect.createSelector(
    // Just a placeholder for now; the ConnectedHeaderCommandBar requires this but we don't have any commands
    [() => { }],
    () => {
        return [];
    }
);

function mapDispatchToProps(dispatch: Dispatch): Partial<ISuiteTreeComponentProps> {
    return {
        onSelectedSuite: (planId, suiteId, suiteName) => dispatch(SuitesActions.changeSelectedSuite(planId, suiteId, suiteName)),
        updateTestSuiteName: (testSuiteId, testSuiteTitle, rootSuiteId) =>
            dispatch(SuitesActions.updateTestSuiteName(testSuiteId, testSuiteTitle, rootSuiteId)),
        changeTreeItemExpandableState: (suiteId, expandedState) => dispatch(SuitesActions.changeTreeItemExpandableState(suiteId, expandedState)),
        saveTestSuiteName: (testPlanId: number, testSuiteId: number, oldSuiteTitle: string, newSuiteTitle: string, rootSuiteId: number) =>
            dispatch(SuitesActions.saveTestSuiteName(testPlanId, testSuiteId, oldSuiteTitle, newSuiteTitle, rootSuiteId)),
        onUpdateReloadState: (state) => dispatch(SuitesActions.updateReloadState(state)),
        updateTestSuiteParent: (testPlanId: number, suiteId: number, targetParentSuiteId: number) =>
            dispatch(SuitesActions.updateTestSuiteParent(testPlanId, suiteId, targetParentSuiteId)),
        reorderTestSuite: (testPlanId: number, parentSuiteId: number, draggedSuiteId: number, targetSuiteId: number, dropAtEnd: boolean) =>
            dispatch(SuitesActions.reorderTestSuite(testPlanId, parentSuiteId, draggedSuiteId, targetSuiteId, dropAtEnd)),
        updateParentAndReorderSuite: (testPlanId: number, parentSuiteId: number, draggedSuiteId: number, targetSuiteId: number, dropAtEnd: boolean) =>
            dispatch(SuitesActions.updateParentAndReorderSuite(testPlanId, parentSuiteId, draggedSuiteId, targetSuiteId, dropAtEnd)),
        showToastNotifications: (toastMessage: string, toastDurationMs: number) =>
            dispatch(SuitesActions.showToastNotifications(toastMessage, toastDurationMs)),
        expandCollapseAllSuites: (expandCollapseBool: boolean) => dispatch(SuitesActions.expandCollapseAllSuites(expandCollapseBool)),
        moveTestCaseToSuite: (
            testPlanId: number,
            sourceSuiteId: number,
            sourceSuiteRevision: number,
            destinationSuiteId: number,
            destinationSuiteRevision: number,
            testCaseIds: number[]
        ) =>
            dispatch(
                SuitesActions.moveTestCaseToSuite(
                    testPlanId,
                    sourceSuiteId,
                    sourceSuiteRevision,
                    destinationSuiteId,
                    destinationSuiteRevision,
                    testCaseIds
                )
            ),
        showTestsFromChildSuitesClick: (planId: number, selectedSuiteId: number, isShowTestsFromChildSuitesEnabled: boolean) =>
            dispatch(TestHubRefreshActions.showTestsFromChildSuitesClick(planId, selectedSuiteId, isShowTestsFromChildSuitesEnabled)),
        fetchTestCasesCount: (planId: number) => dispatch(SuitesActions.getSuiteTestCasesCount(planId, [], [])),
        closeGrid: (planId: number, selectedSuiteId: number) => dispatch(TestHubRefreshActions.closeGrid(planId, selectedSuiteId)),
        filterTestSuites: (keyword: string, callback: any) => dispatch(SuitesActions.filterTestSuites(keyword, callback)),
        publishTelemetry: (action: string, payload: { [key: string]: any }) => dispatch(TelemetryActions.publishTelemetry(action, payload)),
        dispatch
    };
}

function selectHoveredSuite(index: number, suiteSelection: IListSelection) {
    suiteSelection.select(index);
}

/**
 * SuiteTreeFunctionalComponent: This functional component is just a dumb wrapper over SuiteTreeView.
 * We use this because React.useContext can only be invoked in function components. We get the context here and then pass is as props to the class component.
 */
export function SuiteTreeFunctionalComponent(props: ISuiteTreeComponentProps): JSX.Element {
    const testhubRefreshLocalContext = React.useContext(TestHubRefreshLocalContext);

    return <SuiteTreeView {...props} testhubRefreshContext={testhubRefreshLocalContext} />;
}

export const SuiteTreeConnectedView = connect(mapStateToProps, mapDispatchToProps)(SuiteTreeFunctionalComponent);
